import React, { useEffect, useState } from 'react';

import { Tooltip } from '@components/index';

import ConnectingLine from './ConnectingLine';
import StatusDropdown from './StatusDropdown';
import StepIcon from './StepIcon';
import { HorizontalStepperProps } from './types';
import { getStepStatus, getStepStatusLabel } from './utils';

import { STEPPER_CONSTANTS, StepStatus } from '../../../../constants';

const STEPPER_BUTTON_BASE_CLASSES =
  'flex size-8 min-w-[32px] min-h-[32px] items-center justify-center rounded-full border transition-all duration-200';

const HorizontalStepper: React.FC<HorizontalStepperProps> = ({
  steps,
  currentStepIndex,
  activeStepIndex: propActiveStepIndex,
  statusOptions = [],
  onStepClick,
  onStatusChange,
  className = '',
  disabled = false,
  updatingStepId = null
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [internalActiveStepIndex, setInternalActiveStepIndex] =
    useState(currentStepIndex);

  const activeStepIndex =
    propActiveStepIndex !== undefined
      ? propActiveStepIndex
      : internalActiveStepIndex;

  const getStepStyle = (status: StepStatus) => ({
    borderColor:
      status === StepStatus.PENDING
        ? STEPPER_CONSTANTS.COLORS.PENDING_BG
        : STEPPER_CONSTANTS.COLORS.COMPLETED_BG,
    backgroundColor:
      status === StepStatus.PENDING
        ? '#FFFFFF'
        : STEPPER_CONSTANTS.COLORS.COMPLETED_BG
  });

  useEffect(() => {
    if (propActiveStepIndex === undefined) {
      setInternalActiveStepIndex(currentStepIndex);
    }
  }, [currentStepIndex, propActiveStepIndex]);

  const handleStepClick = (index: number) => {
    if (disabled) return;

    if (propActiveStepIndex === undefined) {
      setInternalActiveStepIndex(index);
    }
    onStepClick?.(index);
  };

  const handleStatusChange = (statusId: string) => {
    if (disabled) return;

    const activeStep = steps[activeStepIndex];
    if (activeStep) {
      onStatusChange?.(activeStep.id, statusId);
    }
    setIsDropdownOpen(false);
  };

  const handleToggleDropdown = () => {
    if (disabled) return;

    setIsDropdownOpen(!isDropdownOpen);
  };

  return (
    <div
      className={`flex items-center ${
        disabled ? 'cursor-not-allowed' : ''
      } ${className}`}
    >
      {steps.map((step, index) => {
        const status = getStepStatus(step, index, currentStepIndex);
        const isLast = index === steps.length - 1;
        const isActive = index === activeStepIndex;
        const stepStatusLabel = getStepStatusLabel(step);

        return (
          <React.Fragment key={step.id}>
            {isActive ? (
              <div className="flex items-center">
                <StatusDropdown
                  statusOptions={statusOptions}
                  isDropdownOpen={isDropdownOpen}
                  stepStatusLabel={stepStatusLabel}
                  onToggleDropdown={handleToggleDropdown}
                  onStatusChange={handleStatusChange}
                  disabled={disabled || updatingStepId === step.id}
                  isUpdating={updatingStepId === step.id}
                />
              </div>
            ) : (
              <>
                <button
                  className={`${STEPPER_BUTTON_BASE_CLASSES} ${
                    disabled ? 'cursor-not-allowed' : 'cursor-pointer'
                  } ${updatingStepId === step.id ? 'animate-pulse' : ''}`}
                  style={getStepStyle(status)}
                  onClick={() => handleStepClick(index)}
                  type="button"
                  disabled={updatingStepId === step.id}
                  aria-disabled={disabled || updatingStepId === step.id}
                  data-tooltip-id={`${step.id}-horizontal-stepper`}
                >
                  {updatingStepId === step.id ? (
                    <div className="border-t-blue-600 size-4 animate-spin rounded-full border-2 border-gray-300" />
                  ) : (
                    <StepIcon status={status} />
                  )}
                </button>
                <Tooltip
                  id={`${step.id}-horizontal-stepper`}
                  className="!bg-white !shadow-tooltipShadowNew"
                >
                  <div className="text-12 px-2 py-1 text-gray-800">
                    {stepStatusLabel}
                  </div>
                </Tooltip>
              </>
            )}

            <ConnectingLine
              index={index}
              isLast={isLast}
              currentStepIndex={currentStepIndex}
            />
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default HorizontalStepper;
